import styled, { createGlobalStyle } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { greyMui, rydeColors } from "@ryde/theme";

import {
  RIDES_CALENDAR_CELL_EXPAND_POPPER_MAX_HEIGHT_PX,
  RIDES_CALENDAR_CELL_EXPAND_POPPER_WIDTH_PX,
  RIDES_CALENDAR_CELL_PADDING_PX,
  RIDES_CALENDAR_FOOTER_HEIGHT_PX,
  RIDES_CALENDAR_HEADER_ROW_HEIGHT,
  RIDES_CALENDAR_HOUR_COLUMN_WIDTH_PX,
} from "./rides-calendar.constants";

export const RidesCalendarShellStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "rides-calendar-shell",
})`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: ${rydeColors.smoke};
`;

export const RidesCalendarTruncatedBanner = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "rides-calendar-truncated-banner",
  },
)`
  background-color: ${rydeColors.stationRouteBannerBackground};
  color: ${rydeColors.stationRouteBannerText};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  margin: 8px 12px 0;
`;

export const RidesCalendarEmpty = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "rides-calendar-empty",
})`
  position: absolute;
  inset: ${RIDES_CALENDAR_HEADER_ROW_HEIGHT}px 0 0
    ${RIDES_CALENDAR_HOUR_COLUMN_WIDTH_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${greyMui._600};
  font-size: 14px;
  pointer-events: none;
`;

export const RidesCalendarBodyStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "rides-calendar-body",
})`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`;

export const RidesCalendarGridColumnStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "rides-calendar-grid-column",
  },
)<{ $mapOpen: boolean; $gridPercent: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  flex: ${({ $mapOpen, $gridPercent }) =>
    $mapOpen ? `0 0 ${$gridPercent}%` : "1 1 100%"};
`;

export const RidesCalendarMapStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "rides-calendar-map",
})`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border: 1px solid ${greyMui._300};
  overflow: hidden;

  .rc-map__scroll {
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
    position: relative;
  }
`;

export const RidesCalendarPassengersTooltipWrapper = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "rides-calendar-passengers-tooltip",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 400px;
  max-height: 360px;
  overflow-y: auto;

  .rc-passengers-tooltip__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    white-space: nowrap;
    min-height: 20px;
    column-gap: 2px;
    row-gap: 2px;
  }

  .rc-passengers-tooltip__item {
    padding-inline: 2px;
    font-size: 12px;
    line-height: 1.25;
  }

  .rc-passengers-tooltip__item--name {
    color: ${rydeColors.lightBlue};
    cursor: pointer;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  .rc-passengers-tooltip__divider {
    background: ${rydeColors.white};
    margin-inline: 4px;
    opacity: 0.7;
  }
`;

export const RidesCalendarPopoverGlobalStyles = createGlobalStyle`
  .rc-popover-paper {
    width: ${RIDES_CALENDAR_CELL_EXPAND_POPPER_WIDTH_PX}px;
    max-width: ${RIDES_CALENDAR_CELL_EXPAND_POPPER_WIDTH_PX}px;
    max-height: ${RIDES_CALENDAR_CELL_EXPAND_POPPER_MAX_HEIGHT_PX}px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border: 1px solid ${greyMui._200};
  }

  .rc-popover {
    display: flex;
    flex-direction: column;
    max-height: ${RIDES_CALENDAR_CELL_EXPAND_POPPER_MAX_HEIGHT_PX}px;
  }

  .rc-popover__header {
    padding: 8px 12px;
    border-bottom: 1px solid ${greyMui._200};
    background: ${greyMui._50};
    font-size: 12px;
    font-weight: 600;
    color: ${greyMui._700};
    text-transform: uppercase;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .rc-popover__header-count {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 9px;
    background: ${rydeColors.selectedBookingCalendarBackground};
    color: ${rydeColors.venetianNights};
    border: 1px solid ${rydeColors.venetianNights};
  }

  .rc-popover__list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    overflow-y: auto;
    flex: 1 1 auto;
  }

  .rc-chip {
    display: flex;
    align-items: center;
    background: ${rydeColors.white};
    border: 1px solid ${greyMui._200};
    border-inline-start-width: 3px;
    border-radius: 6px;
    padding: 4px 8px 4px 5px;
    cursor: pointer;
    font-size: 11px;
    line-height: 1.2;
    color: ${greyMui._900};
    text-align: start;
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    gap: 6px;
    min-height: 38px;
  }

  .rc-chip:hover {
    border-color: ${rydeColors.steel};
    background: ${greyMui._50};
  }

  .rc-chip:focus-visible {
    outline: 2px solid ${rydeColors.steel};
    outline-offset: -2px;
  }

  .rc-chip__select {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
  }

  .rc-chip__route-color {
    display: inline-flex;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.2);
    box-sizing: border-box;
  }

  .rc-chip__body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    align-items: stretch;
    gap: 2px;
  }

  .rc-chip__line {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
  }

  .rc-chip__line--head {
    font-size: 12px;
    font-weight: 500;
  }

  .rc-chip__direction {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    color: ${rydeColors.venetianNights};
  }

  .rc-chip__direction svg {
    width: 28px;
    height: 28px;
  }

  .rc-chip__time {
    font-weight: 700;
    color: ${rydeColors.princetonPurple};
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    flex-shrink: 0;
  }

  .rc-chip__supplier {
    flex: 1 1 0;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    overflow: hidden;
    font-weight: 600;
    color: ${greyMui._900};
  }

  .rc-chip__supplier-text {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rc-chip__course {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  .rc-chip__line--route {
    font-size: 11px;
    color: ${greyMui._700};
    gap: 4px;
  }

  .rc-chip__from,
  .rc-chip__to {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 0;
  }

  .rc-chip__arrow {
    flex-shrink: 0;
    color: ${rydeColors.venetianNights};
    font-weight: 700;
  }

  .rc-chip__passengers {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    border-radius: 9px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    background: ${rydeColors.selectedBookingCalendarBackground};
    color: ${rydeColors.venetianNights};
    border: 1px solid ${rydeColors.venetianNights};
    font-variant-numeric: tabular-nums;
  }

  .rc-chip__passengers-inner {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 1px 6px 1px 4px;
  }

  .rc-chip__passengers-icon {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }

  .rc-chip__passengers-count {
    line-height: 1;
  }

  .rc-chip__passengers--empty {
    background: ${greyMui._100};
    color: ${greyMui._700};
    border-color: ${greyMui._300};
  }

  .rc-chip__occupancy {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;

    svg {
      margin-inline-end: 2px;
      width: 13px;
      height: 13px;
    }
  }

  .rc-chip--selected {
    border-color: ${rydeColors.venetianNights};
    background: ${rydeColors.selectedBookingCalendarBackground};
  }

  .rc-chip--highlighted {
    outline: 3px solid ${rydeColors.dragBorderColor};
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25);
    animation: rcChipHighlightPulse 1.4s ease-in-out infinite;
  }

  @keyframes rcChipHighlightPulse {
    0%,
    100% {
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25);
    }
    50% {
      box-shadow:
        0 2px 5px rgba(0, 0, 0, 0.25),
        0 0 0 4px rgba(179, 136, 255, 0.25);
    }
  }
`;

export const RidesCalendarGridStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "rides-calendar-grid",
})`
  background: ${rydeColors.white};
  border: 1px solid ${greyMui._200};
  border-radius: 8px;
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;

  .rc-corner-cell {
    background: ${greyMui._200};
    border-inline-end: 1px solid ${greyMui._300};
    border-bottom: 1px solid ${greyMui._300};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: ${greyMui._700};
    text-transform: uppercase;
    padding: 0 8px;

    .rc-corner-cell__select {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    .rc-corner-cell__label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .rc-head-cell {
    background: ${greyMui._100};
    border-inline-end: 1px solid ${greyMui._300};
    border-bottom: 1px solid ${greyMui._300};
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    grid-template-areas: "select dow" "select dmy";
    align-items: center;
    font-size: 12px;
    color: ${greyMui._800};
    padding: 4px 8px;
    gap: 2px 8px;

    .rc-head-cell__select {
      grid-area: select;
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    .rc-head-cell__dow {
      grid-area: dow;
      font-weight: 600;
      text-transform: uppercase;
      color: ${greyMui._600};
      font-size: 11px;
      text-align: start;
    }

    .rc-head-cell__dmy {
      grid-area: dmy;
      font-size: 12px;
      font-weight: 600;
      text-align: start;
    }
  }

  .rc-head-cell--today {
    background: ${rydeColors.selectedBookingCalendarBackground};

    .rc-head-cell__dmy {
      color: ${rydeColors.steel};
    }
  }

  .rc-hour-cell {
    background: ${greyMui._100};
    border-inline-end: 1px solid ${greyMui._300};
    border-bottom: 1px solid ${greyMui._200};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 8px;
    gap: 6px;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${greyMui._700};

    .rc-hour-cell__select {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    .rc-hour-cell__label {
      font-weight: 600;
      white-space: nowrap;
    }
  }

  .rc-body-cell {
    border-inline-end: 1px solid ${greyMui._200};
    border-bottom: 1px solid ${greyMui._200};
    background: ${rydeColors.white};
    overflow: hidden;
    padding: ${RIDES_CALENDAR_CELL_PADDING_PX}px
      ${RIDES_CALENDAR_CELL_PADDING_PX}px
      ${RIDES_CALENDAR_FOOTER_HEIGHT_PX + RIDES_CALENDAR_CELL_PADDING_PX}px;
    position: relative;

    &:hover {
      background: ${rydeColors.gridCellHover};
    }
  }

  .rc-body-cell--selected {
    box-shadow: inset 0 0 0 2px ${rydeColors.venetianNights};
    background: ${rydeColors.selectedBookingCalendarBackground};
  }

  .rc-body-cell__expand {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: ${rydeColors.venetianNights};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
    transition:
      background 0.12s ease-in-out,
      color 0.12s ease-in-out;

    &:hover,
    &:focus-visible {
      background: ${rydeColors.venetianNights};
      color: ${rydeColors.white};
    }
  }

  .rc-body-cell--expand-open .rc-body-cell__expand {
    background: ${rydeColors.venetianNights};
    color: ${rydeColors.white};
  }

  .rc-body-cell__add {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: ${rydeColors.venetianNights};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
    transition:
      background 0.12s ease-in-out,
      color 0.12s ease-in-out;

    &:hover,
    &:focus-visible {
      background: ${rydeColors.venetianNights};
      color: ${rydeColors.white};
    }

    .rc-body-cell__add-icon {
      width: 14px;
      height: 14px;
    }
  }

  .rc-body-cell__list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 100%;
    overflow-y: auto;
  }

  .rc-body-cell__more {
    display: block;
    background: none;
    border: none;
    color: ${rydeColors.steel};
    font-size: 11px;
    margin-top: 2px;
    cursor: pointer;
    padding: 0;
    text-align: start;
    flex-shrink: 0;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  .rc-body-cell__footer {
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    height: ${RIDES_CALENDAR_FOOTER_HEIGHT_PX}px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 0 6px 0 8px;
    background: ${rydeColors.white};
    border-top: 1px solid ${greyMui._200};
    opacity: 0;
    transition: opacity 0.12s ease-in-out;
    z-index: 1;
  }

  .rc-body-cell:hover .rc-body-cell__footer,
  .rc-body-cell__footer--pinned {
    opacity: 1;
    background: ${greyMui._100};
  }

  .rc-body-cell__footer-start {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
  }

  .rc-body-cell__select-link {
    background: none;
    border: none;
    color: ${rydeColors.venetianNights};
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  .rc-body-cell__select-link--deselect {
    color: ${rydeColors.crisisRed};
  }

  .rc-body-cell__footer-end {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .rc-body-cell__badge {
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 2px 6px;
    border-radius: 9px;
    background: ${greyMui._100};
    color: ${greyMui._700};
    border: 1px solid ${greyMui._300};
    white-space: nowrap;
  }

  .rc-body-cell__badge--selected {
    color: ${rydeColors.venetianNights};
    background: ${rydeColors.selectedBookingCalendarBackground};
    border-color: ${rydeColors.venetianNights};
  }
`;
